import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Chip from '@mui/material/Chip';
import PublicIcon from '@mui/icons-material/Public';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const groups = {
  "566bfb38-de34-4054-be17-485f8d8ac95d8": "Безперебійники",
  "95bcec89-8380-4bf0-9ab6-4cf6a1b4f50e": "Ваги",
  "2333a737-15a0-4fee-99db-14f6347da583": "Грошові скриньки",
  "3a2edf1d-07eb-40ee-b21f-56748d9a5253": "Зарядні станції",
  "a058900c-ac3b-47fb-877b-9a74cba588d5": "Зчитувачі карток",
  "4deb4f17-073f-4cca-83e4-c057cdbc8496": "Карточки",
  "72241b41-70c3-4793-8dcf-092461fd4d54": "Комплектні комплектуючі",
  "974714fc-9dc0-4e05-927d-c0469a1a1449": "Некомплектні комплектуючі",
  "732dc1ee-0796-4d85-aa7a-0ce599e7345e": "Моноблоки Нові",
  "a4fb616d-1e1c-4435-9639-cd97d40b28e7": "Моноблоки б/у",
  "5ec5a45e-8f0e-428e-9cb6-5ac23f1ca530": "Наліпки Windows",
  "6e734c6c-5fc3-4038-a8f9-f2d0e331c4ef": "Принтери нові",
  "bdafbc92-dab6-4739-8e8f-f62186425998": "Принтери б/у",
  "17914df4-795e-468d-9bc0-5416a821223b": "Сканери ШК та QR",
};


const API_CONFIG = {
  BASE_URL: 'https://hub2.daolog.net',
  ENDPOINTS: {
    AUTH: '/api/syrve-auth',
    PRODUCTS: '/api/syrve-products',
    REPORTS: '/api/syrve-balance-report',
    HEALTH: '/api/syrve-health',
    TEST: '/api/test'
  }
};


const ALLOWED_IPS = [

  '91.196.55.16',
  '194.44.253.68',
  '10.120.131.1',
  '192.168.1.100',
  '192.168.1.101',
  '94.231.79.13',
  '10.0.0.50',
  '::1',
  '127.0.0.1',
];


const IS_IP_RESTRICTION_ENABLED = true; 


const IP_SERVICES = [
  { url: 'https://api.ipify.org/?format=json', parser: (data) => data.ip },
  { url: 'https://api.myip.com', parser: (data) => data.ip },
  { url: 'https://ipapi.co/json/', parser: (data) => data.ip },
  { url: 'https://jsonip.com', parser: (data) => data.ip },
  { url: 'https://api.ip.sb/ip', parser: (data) => data.trim() },
  { url: 'https://ip.seeip.org/json', parser: (data) => data.ip },
];


const getClientIP = async () => {

  for (const service of IP_SERVICES) {
    try {
      console.log(`Спроба отримати IP через: ${service.url}`);
      const response = await axios.get(service.url, {
        timeout: 3000,
        headers: {
          'Accept': 'application/json',
        }
      });
      
      let ip = service.parser(response.data);
      if (ip && typeof ip === 'string' && ip.trim() !== '') {
        console.log(`IP отримано через ${service.url}: ${ip}`);
        return ip.trim();
      }
    } catch (error) {
      console.log(`Помилка через ${service.url}:`, error.message);

    }
  }
  
  return null;
};


const checkIPAccess = (clientIP) => {
  if (!clientIP) return false;
  

  if (!IS_IP_RESTRICTION_ENABLED) {
    console.log('УВАГА: Обмеження за IP ТИМЧАСОВО ВИМКНЕНО!');
    return true;
  }
  

  const hasAccess = ALLOWED_IPS.includes(clientIP);
  
  if (!hasAccess) {
    console.log(`Доступ заборонено для IP: ${clientIP}`);
    console.log('Дозволені IP:', ALLOWED_IPS);
  } else {
    console.log(`Доступ дозволено для IP: ${clientIP}`);
  }
  
  return hasAccess;
};

function App() {
  const [data, setData] = useState(null);
  const [key, setKey] = useState(null);
  const [products, setProducts] = useState(null);
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [serverStatus, setServerStatus] = useState('checking');
  const [clientIP, setClientIP] = useState(null);
  const [ipAccessGranted, setIpAccessGranted] = useState(false);
  const [ipCheckComplete, setIpCheckComplete] = useState(false);
  const [ipCheckAttempts, setIpCheckAttempts] = useState(0);

  useEffect(() => {
    checkIPAndLoadData();
  }, []);

  const checkIPAndLoadData = async () => {
    try {
      setIpCheckComplete(false);
      setIpCheckAttempts(prev => prev + 1);
      
      const ip = await getClientIP();
      setClientIP(ip);
      
      if (ip) {
        console.log('Ваша IP-адреса:', ip);
        const hasAccess = checkIPAccess(ip);
        setIpAccessGranted(hasAccess);
        
        if (hasAccess) {
        
          await loadData();
        } else {
        
          setError(`Доступ заборонено для IP-адреси: ${ip}`);
          setLoading(false);
        }
      } else {
        console.log('Не вдалося визначити IP через зовнішні сервіси');
        
        if (ipCheckAttempts > 2) {
          setError('Не вдалося визначити вашу IP-адресу. Можливо, блокувальник реклами заважає.');
          setLoading(false);
        } else {
     
          setTimeout(() => {
            checkIPAndLoadData();
          }, 1000);
        }
      }
      
      setIpCheckComplete(true);
    } catch (err) {
      console.error('Помилка перевірки IP:', err);
      setError('Помилка перевірки доступу');
      setLoading(false);
      setIpCheckComplete(true);
    }
  };

  
  const testServerConnection = async () => {
    try {
      console.log('Testing server connection...');
      const response = await axios.get(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TEST}`, {
        timeout: 5000
      });
      
      if (response.data && response.data.success) {
        setServerStatus('connected');
        return true;
      } else {
        setServerStatus('error');
        return false;
      }
    } catch (err) {
      console.error('Server connection test failed:', err.message);
      setServerStatus('error');
      return false;
    }
  };

 
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('=== START LOADING DATA ===');
      
  
      const serverOk = await testServerConnection();
      if (!serverOk) {
        throw new Error('Не вдалося підключитися до сервера. Перевірте з\'єднання.');
      }
      
 
      console.log('Getting auth key...');
      const authResponse = await axios.post(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH}`,
        {
          login: 'vik_syrve',
          pass: '766286d4a36c77f5008d7a583a8ebcf9b2f6ea81'
        },
        {
          timeout: 15000,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Auth response:', authResponse.data);
      
      if (!authResponse.data.success || !authResponse.data.token) {
        throw new Error(`Помилка авторизації: ${authResponse.data.message || 'Невідома помилка'}`);
      }
      
      const apiKey = authResponse.data.token;
      setKey(apiKey);
      console.log('API key received');
      
   
      console.log('Getting products...');
      const productsResponse = await axios.get(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PRODUCTS}`,
        {
          params: { key: apiKey },
          timeout: 30000
        }
      );
      
      console.log('Products response:', productsResponse.data);
      
      if (!productsResponse.data.success) {
        throw new Error(`Помилка завантаження продуктів: ${productsResponse.data.message}`);
      }
      
      const productsData = productsResponse.data.data;
      setProducts(productsData);
      console.log('Products loaded:', productsData?.length || 0, 'items');
      

      console.log('Getting balance report...');
      const timestamp = new Date().toISOString().split('T')[0] + 'T23:59:59';
      
      const reportResponse = await axios.get(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS}`,
        {
          params: { 
            key: apiKey,
            timestamp 
          },
          timeout: 30000
        }
      );
      
      console.log('Report response:', reportResponse.data);
      
      if (!reportResponse.data.success) {
        throw new Error(`Помилка завантаження звіту: ${reportResponse.data.message}`);
      }

      const reportData = reportResponse.data.data;
      const storeId = "6a2f3062-c0e8-4113-9188-aac45589ceb0";
      
      console.log('Total report items:', reportData?.length || 0);
      
      const dataWithProductNames = (reportData || [])
        .filter(item => item && item.store === storeId)
        .map(item => {
          const product = (productsData || []).find(p => p && p.id === item.product);
          return {
            id: item.product,
            name: product ? product.name : 'Невідомий продукт',
            sum: item.sum || 0,
            amount: item.amount || 0,
            parent: product ? product.parent : null
          };
        })
        .filter(item => item.parent !== null && item.amount > 0);
      
      console.log('Filtered items:', dataWithProductNames.length);
      
   
      const groupedData = dataWithProductNames.reduce((acc, item) => {
        const group = groups[item.parent];
        const groupName = group || "Інше";
        
        if (!acc[groupName]) {
          acc[groupName] = { items: [], totalSum: 0, totalAmount: 0 };
        }
        
        acc[groupName].items.push(item);
        acc[groupName].totalSum += item.sum;
        acc[groupName].totalAmount += item.amount;
        return acc;
      }, {});
      

      const sortedGroupedData = Object.keys(groupedData)
        .sort()
        .reduce((acc, key) => {
          acc[key] = groupedData[key];
          return acc;
        }, {});
      
      setData(sortedGroupedData);
      setLastUpdate(new Date());
      setLoading(false);
      
      console.log('=== DATA LOADING COMPLETED ===');
      console.log('Groups loaded:', Object.keys(sortedGroupedData).length);
      
    } catch (err) {
      console.error('=== LOAD DATA ERROR ===');
      console.error('Error:', err);
      console.error('Response data:', err.response?.data);
      console.error('Response status:', err.response?.status);
      
      let errorMessage = 'Невідома помилка';
      
      if (err.code === 'ECONNABORTED') {
        errorMessage = 'Час очікування вийшов. Спробуйте ще раз.';
      } else if (err.response) {
        errorMessage = `Помилка сервера (${err.response.status}): ${err.response.data?.message || err.message}`;
      } else if (err.request) {
        errorMessage = `Немає відповіді від сервера: ${err.message}`;
      } else {
        errorMessage = `Помилка запиту: ${err.message}`;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleClick = (group) => {
    setOpen(prevOpen => ({ 
      ...prevOpen, 
      [group]: !prevOpen[group] 
    }));
  };

  const formatNumber = (num) => {
    if (isNaN(num) || num === 0) return '0.00';
    return new Intl.NumberFormat('uk-UA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatPricePerUnit = (total, amount) => {
    if (amount === 0) return '0.00';
    return formatNumber(total / amount);
  };

  const getStatusColor = () => {
    switch(serverStatus) {
      case 'connected': return 'success';
      case 'error': return 'error';
      default: return 'info';
    }
  };

  if (!ipCheckComplete) {
    return (
      <Container maxWidth="lg">
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="100vh"
          flexDirection="column"
        >
          <CircularProgress size={60} />
          <Box mt={3}>
            <Typography variant="h6" color="primary">
              Перевірка доступу...
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Спроба {ipCheckAttempts}
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }


  if (!ipAccessGranted && clientIP) {
    return (
      <Container maxWidth="lg">
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="100vh"
          flexDirection="column"
        >
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              maxWidth: 500,
              textAlign: 'center',
              borderRadius: 2,
              borderTop: '4px solid #f44336'
            }}
          >
            <BlockIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" color="error" gutterBottom fontWeight="bold">
              Доступ заборонено
            </Typography>
            <Typography variant="body1" paragraph color="text.secondary">
              Ваша IP-адреса не має дозволу на доступ до цього ресурсу.
            </Typography>
            
            <Paper 
              elevation={0} 
              sx={{ 
                bgcolor: '#ffebee', 
                p: 2, 
                borderRadius: 1,
                mb: 3
              }}
            >
              <Typography variant="subtitle2" color="error" gutterBottom>
                Ваша IP-адреса:
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="error">
                {clientIP}
              </Typography>
            </Paper>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              Для отримання доступу зверніться до адміністратора системи.
            </Typography>
            
            <Button 
              variant="outlined" 
              onClick={checkIPAndLoadData}
              startIcon={<RefreshIcon />}
              sx={{ mt: 2 }}
            >
              Перевірити знову
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (loading && !data) {
    return (
      <Container maxWidth="lg">
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="100vh"
          flexDirection="column"
        >
          <CircularProgress size={60} />
          <Box mt={3}>
            <Typography variant="h6" color="primary">
              Завантаження даних зі складу...
            </Typography>
            {clientIP && (
              <Box mt={2} display="flex" alignItems="center" justifyContent="center">
                <Chip 
                  icon={<PublicIcon />}
                  label={`Ваша IP: ${clientIP}`}
                  color="info"
                  variant="outlined"
                />
              </Box>
            )}
            <Box mt={1} display="flex" alignItems="center" justifyContent="center">
              <Typography variant="body2" color="text.secondary">
                Статус сервера: 
              </Typography>
              <Box ml={1}>
                <Typography 
                  variant="body2" 
                  color={getStatusColor()}
                  fontWeight="bold"
                >
                  {serverStatus === 'connected' ? 'Підключено' : 
                   serverStatus === 'error' ? 'Помилка' : 'Перевірка...'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold" color="primary">
              Залишки на складі
            </Typography>
            {lastUpdate && (
              <Typography variant="body2" color="text.secondary" mt={1}>
                Останнє оновлення: {lastUpdate.toLocaleString('uk-UA')}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            {clientIP && (
              <Chip 
                icon={<PublicIcon />}
                label={clientIP}
                color="success"
                variant="outlined"
              />
            )}
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading}
              size="large"
            >
              {loading ? 'Оновлення...' : 'Оновити дані'}
            </Button>
          </Box>
        </Box>
        
        {}
        <Box mb={3}>
          <Alert 
            severity={getStatusColor()} 
            sx={{ mb: 2 }}
            action={
              serverStatus === 'error' && (
                <Button color="inherit" size="small" onClick={loadData}>
                  Повторити
                </Button>
              )
            }
          >
            Статус сервера: {serverStatus === 'connected' ? 'Підключено' : 
                            serverStatus === 'error' ? 'Помилка підключення' : 'Перевірка...'}
          </Alert>
        </Box>
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={checkIPAndLoadData}>
                Спробувати знову
              </Button>
            }
          >
            <Typography fontWeight="bold">Помилка:</Typography>
            {error}
          </Alert>
        )}
        
        {data && Object.keys(data).length > 0 ? (
          <>
            {Object.entries(data).map(([group, {items, totalAmount, totalSum}]) => (
              <Box key={group} mb={3}>
                <ListItem 
                  button 
                  onClick={() => handleClick(group)}
                  sx={{
                    backgroundColor: 'primary.light',
                    borderRadius: '8px',
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      '& .MuiListItemText-primary': {
                        color: 'white'
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'white'
                      }
                    }
                  }}
                >
                  <ListItemText 
                    primary={
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <Typography variant="h6" component="span" fontWeight="bold">
                          {group}
                        </Typography>
                        <Typography variant="h6" component="span" fontWeight="bold">
                          {totalAmount} шт. • {formatNumber(totalSum)} $
                        </Typography>
                      </Box>
                    }
                  />
                  {open[group] ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                
                <Collapse in={open[group]} timeout="auto" unmountOnExit>
                  <TableContainer 
                    component={Paper} 
                    sx={{ 
                      mb: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '8px'
                    }}
                  >
                    <Table sx={{ minWidth: 700 }} aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Назва продукту</StyledTableCell>
                          <StyledTableCell align="right">Ціна за одиницю, $</StyledTableCell>
                          <StyledTableCell align="right">Кількість</StyledTableCell>
                          <StyledTableCell align="right">Загальна вартість, $</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item, index) => (
                          <StyledTableRow key={`${item.id}-${index}`}>
                            <StyledTableCell component="th" scope="row">
                              <Typography>{item.name}</Typography>
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              <Typography fontWeight="medium">
                                {formatPricePerUnit(item.sum, item.amount)}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              <Typography fontWeight="medium">
                                {item.amount}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              <Typography fontWeight="bold" color="primary">
                                {formatNumber(item.sum)}
                              </Typography>
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                        {}
                        <StyledTableRow sx={{ backgroundColor: 'success.light' }}>
                          <StyledTableCell>
                            <Typography fontWeight="bold">Разом по групі:</Typography>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Typography fontWeight="bold">
                              {formatPricePerUnit(totalSum, totalAmount)}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Typography fontWeight="bold">
                              {totalAmount}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Typography variant="h6" fontWeight="bold" color="success.dark">
                              {formatNumber(totalSum)} $
                            </Typography>
                          </StyledTableCell>
                        </StyledTableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Box>
            ))}
          </>
        ) : !loading && !error ? (
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography fontWeight="bold">Інформація:</Typography>
            Немає даних для відображення. Можливо, на складі немає залишків або вони ще не завантажилися.
          </Alert>
        ) : null}
        
        {}
        <Box mt={4} pt={3} borderTop="1px solid" borderColor="divider">
       
        </Box>
      </Box>
    </Container>
  );
}

export default App;